import React, {useContext, useState, useCallback} from "react";
import {Link as RouterLink, useLocation, useNavigate} from "react-router-dom";
import {
    Tab,
    Tabs,
    Box,
    IconButton,
    TextField,
    Button,
    Drawer,
    Typography,
    Alert, CircularProgress, Snackbar
} from "@mui/material";
import logo from "../logo.png";
import {
    Close as CloseIcon,
    FeedbackRounded as FeedbackRoundedIcon,
    HomeOutlined,
    AdminPanelSettings,
    Radar as RadarIconMui,
    Public,
    Article as ArticleIconMui,
    MenuBook,
    Edit,
    BarChart,
    Send,
    AutoAwesome,
    Share,
    Speed,
    Hearing,
} from "@mui/icons-material";
import { AppStageBadge } from "../components/AppStageBadge";
import {AccountIconButton} from "../components/account/AccountIconButton";
import LayoutNewsClusters from "./LayoutNewsClusters";
import LayoutRadarAdditionalFeeds from "./LayoutRadarAdditionalFeeds";
import {LayoutAdmin} from "./LayoutAdmin";
import {HeaderNavTabs} from "./HeaderNavTabs";
import {LayoutDashboard} from "./LayoutDashboard";
import ProfilePicture from "../components/account/ProfilePicture";
import {ChangePasswordDialog} from "../components/account/ChangePasswordDialog";
import {api} from "../api/apiService";
import Collapse from "@mui/material/Collapse";
import {UserContext} from "../contexts/UserContext";
import {AuthContext} from "../auth/AuthContext";
import LayoutLibrary from "./LayoutLibrary";
import LayoutResearch from "./LayoutResearch";
import AnalyticProfileSelector from "../components/AnalyticProfileSelector";
import {Features} from "../components/Features";
import {DistributionHub} from "../components/DistributionHub";
import LayoutIdeaSparker from "./LayoutIdeaSparker";
import LayoutSocialStudio from "./LayoutSocialStudio";
import LayoutSocialListening from "./LayoutSocialListening";
import LayoutWorldInsights from "./LayoutWorldInsights";
import LayoutFastTracking from "./LayoutFastTracking";
import { OpenRadarAttachContext } from "../contexts/OpenRadarAttachContext";
import {AppView, parseAppLocation, RADAR_TABS, radarPath, VIEW_PATHS} from "../routing/appRoutes";
import {RouteMessage} from "../routing/RouteMessage";
import {ignorePlainClick} from "../routing/linkClick";

/**
 * Content Radar subtabs.
 *
 * The previous layout exposed Custom Sources and Relevant Articles as two
 * separate tabs even though they shared the same component, columns, and
 * filters — only the upstream `type` differed. They are now a single
 * "Articles" tab with a Source-kind filter inside the filters drawer
 * (Custom / Relevant / Both).
 */
const SUB_TAB_WORLD = RADAR_TABS.indexOf('world');
const SUB_TAB_CLUSTERS = RADAR_TABS.indexOf('clusters');
const SUB_TAB_ARTICLES = RADAR_TABS.indexOf('articles');

// Permission resource (read) each feature view needs; keep in sync with the Home cards (Features.tsx).
const VIEW_PERMISSION: Partial<Record<string, string>> = {
    news: 'content_radar',
    research: 'notebooks',
    library: 'notebooks',
    distribution: 'notebooks',
    dashboard: 'analytic_profiles',
    idea_sparker: 'idea_sparker',
    social_studio: 'social_studio',
    social_listening: 'social_listening',
    fast_tracking: 'analytic_profiles',
};

const LayoutMain = () => {
    const {user, setUser} = useContext(UserContext);
    const {logout} = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    // The URL is the source of truth for what is shown (see routing/appRoutes.ts).
    const appLocation = parseAppLocation(location.pathname);
    const currentView: AppView | null = appLocation?.view ?? null;
    const selectedSubTab =
        appLocation?.view === 'news' ? Math.max(0, RADAR_TABS.indexOf(appLocation.section as typeof RADAR_TABS[number])) : 0;
    /** When set, Clusters tab pre-selects this notebook in Save to notebook, then clears via callback. */
    const [radarPreselectNotebookIdForSave, setRadarPreselectNotebookIdForSave] = useState<string | null>(null);

    const openRadarToAttachClusters = useCallback((notebookId: string) => {
        setRadarPreselectNotebookIdForSave(notebookId);
        navigate(radarPath('clusters'));
    }, [navigate]);

    const clearRadarPreselectNotebookIdForSave = useCallback(() => {
        setRadarPreselectNotebookIdForSave(null);
    }, []);

    const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [feedbackDrawerOpen, setFeedbackDrawerOpen] = useState(false);
    const [feedbackTitle, setFeedbackTitle] = useState('');
    const [feedbackDescription, setFeedbackDescription] = useState('');
    const [feedbackError, setFeedbackError] = useState('');
    const [feedbackSuccess, setFeedbackSuccess] = useState('');
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

    const handleFeedbackSubmit = async () => {
        setFeedbackError('');
        setFeedbackSuccess('');
        if (!feedbackTitle.trim() || !feedbackDescription.trim()) {
            setFeedbackError('Please provide both title and description.');
            return;
        }
        setFeedbackSubmitting(true);
        try {
            await api.post('/api/feedback/general', {
                title: feedbackTitle.trim(),
                description: feedbackDescription.trim(),
            });
            setFeedbackSuccess('Feedback submitted successfully!');
            setFeedbackTitle('');
            setFeedbackDescription('');
            setFeedbackDrawerOpen(false);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'An unexpected error occurred.';
            setFeedbackError(message);
        } finally {
            setFeedbackSubmitting(false);
        }
    };

    const openFeedbackDrawer = () => {
        setProfileDrawerOpen(false);
        setFeedbackDrawerOpen(true);
    };

    const openProfileDrawer = () => {
        setFeedbackDrawerOpen(false);
        setProfileDrawerOpen(true);
    };

    // Get available news subtabs
    const getAvailableNewsSubTabs = (): Array<{ index: number; label: React.ReactNode }> => {
        return [
            { index: SUB_TAB_WORLD, label: 'World insights' },
            { index: SUB_TAB_CLUSTERS, label: 'Clusters' },
            { index: SUB_TAB_ARTICLES, label: 'Articles' },
        ];
    };

    // Get the current sequential tab index for the Tabs component
    const getCurrentSequentialTabIndex = () => {
        const availableTabs = getAvailableNewsSubTabs();
        const currentTabIndex = availableTabs.findIndex(tab => tab.index === selectedSubTab);
        return currentTabIndex >= 0 ? currentTabIndex : 0;
    };

    const showAdmin = Boolean(
        user?.permissions?.users?.read ||
        user?.permissions?.users?.create ||
        user?.permissions?.analytic_profiles?.read ||
        user?.permissions?.analytic_profiles?.create ||
        user?.permissions?.company_profile?.read ||
        user?.permissions?.company_profile?.update
    );

    const getMainTabs = () => {
        const tabs = [
            {
                label: "Home",
                content: (
                    <Box sx={{ backgroundColor: '#0a0a0a', minHeight: 'calc(100vh - 200px)', padding: 0 }}>
                        <Features />
                    </Box>
                )
            }
        ];

        if (showAdmin) {
            tabs.push({
                label: "Admin",
                content: (
                    <div>
                        <LayoutAdmin />
                    </div>
                ),
            });
        }

        return tabs;
    };

    const renderCurrentView = () => {
        if (currentView === null) {
            return <RouteMessage title="Page not found" message="This link doesn't match any page in Flock." />;
        }

        // A shared link may point at a view this user can't open; say so instead of silently showing Home.
        const requiredResource = VIEW_PERMISSION[currentView];
        const noAccess =
            currentView === 'admin' ? !showAdmin : Boolean(requiredResource && !user?.permissions?.[requiredResource]?.read);
        if (noAccess) {
            return (
                <RouteMessage
                    title="No access"
                    message="You don't have permission to view this page. If you think you should, ask your administrator."
                />
            );
        }

        switch (currentView) {
            case 'admin':
                return getMainTabs()[1]?.content;
            case 'home':
                return (
                    <Box sx={{ backgroundColor: '#0a0a0a', minHeight: 'calc(100vh - 200px)', padding: 0 }}>
                        <Features />
                    </Box>
                );
            case 'research':
                return (
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                        <LayoutResearch />
                    </Box>
                );
            case 'news':
                return (
                    <Box
                        sx={{
                            bgcolor: '#0a0a0a',
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            height: 'calc(100vh - 80px)',
                        }}
                    >
                        <Tabs
                            // Tabs are router links (no onChange, so modified clicks only open a new tab).
                            value={getCurrentSequentialTabIndex()}
                            aria-label="Content radar subtabs"
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                flexShrink: 0,
                                px: 2,
                                pt: 2,
                                borderBottom: '1px solid #27272a',
                                '& .MuiTab-root': {
                                    color: '#a1a1aa',
                                    '&.Mui-selected': {
                                        color: '#fafafa',
                                    },
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#fafafa',
                                },
                            }}
                        >
                            {getAvailableNewsSubTabs().map((tab) => (
                                <Tab
                                    key={tab.index}
                                    label={tab.label}
                                    component={RouterLink}
                                    to={radarPath(RADAR_TABS[tab.index])}
                                    onClick={tab.index === selectedSubTab ? ignorePlainClick : undefined}
                                    sx={{ minWidth: '120px', maxWidth: 200, whiteSpace: 'normal', wordWrap: 'break-word' }}
                                />
                            ))}
                        </Tabs>
                        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 0, display: 'flex', flexDirection: 'column' }}>
                            {selectedSubTab === SUB_TAB_WORLD && <LayoutWorldInsights embedded />}
                            {selectedSubTab === SUB_TAB_CLUSTERS && (
                                <LayoutNewsClusters
                                    preselectNotebookIdForSave={radarPreselectNotebookIdForSave}
                                    onConsumedPreselectNotebookIdForSave={clearRadarPreselectNotebookIdForSave}
                                />
                            )}
                            {selectedSubTab === SUB_TAB_ARTICLES && (
                                // Articles consolidates the former Custom Sources + Relevant Articles tabs;
                                // the Source-kind filter inside the drawer narrows to manual_source / single_article.
                                <LayoutRadarAdditionalFeeds />
                            )}
                        </Box>
                    </Box>
                );
            case 'library':
                return <div><LayoutLibrary/></div>;
            case 'dashboard':
                return <div><LayoutDashboard/></div>;
            case 'distribution':
                return <div><DistributionHub /></div>;
            case 'idea_sparker':
                return (
                    <LayoutIdeaSparker
                        onArticleDraftReady={() => {
                            navigate(VIEW_PATHS.library);
                        }}
                    />
                );
            case 'social_studio':
                return (
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                        <LayoutSocialStudio />
                    </Box>
                );
            case 'social_listening':
                return (
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                        <LayoutSocialListening />
                    </Box>
                );
            case 'fast_tracking':
                return (
                    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                        <LayoutFastTracking />
                    </Box>
                );
            default:
                return null;
        }
    };

    const appStage = (() => {
        switch (currentView) {
            case 'admin':
                return showAdmin ? { icon: AdminPanelSettings, label: 'Admin' } : null;
            case 'home':
                return { icon: HomeOutlined, label: 'Home' };
            case 'news':
                if (selectedSubTab === SUB_TAB_WORLD) return { icon: Public, label: 'Content Radar' };
                if (selectedSubTab === SUB_TAB_CLUSTERS) return { icon: RadarIconMui, label: 'Clusters' };
                if (selectedSubTab === SUB_TAB_ARTICLES) return { icon: ArticleIconMui, label: 'Articles' };
                return { icon: RadarIconMui, label: 'Content Radar' };
            case 'research':
                return { icon: MenuBook, label: 'Research' };
            case 'library':
                return { icon: Edit, label: 'Draft Creator' };
            case 'dashboard':
                return { icon: BarChart, label: 'Analytics' };
            case 'distribution':
                return { icon: Send, label: 'Distribution Hub' };
            case 'idea_sparker':
                return { icon: AutoAwesome, label: 'Idea Sparker' };
            case 'social_studio':
                return { icon: Share, label: 'Social Studio' };
            case 'social_listening':
                return { icon: Hearing, label: 'Social Listening' };
            case 'fast_tracking':
                return { icon: Speed, label: 'Fast tracking' };
            default:
                return null;
        }
    })();

    return (
        <OpenRadarAttachContext.Provider value={openRadarToAttachClusters}>
        <Box style={{backgroundColor: '#0a0a0a'}}>
            <Box display="flex" alignItems="center" sx={{ backgroundColor: '#1a1a1a', py: 1, px: 2, height: '80px' }}>
                {/* Left: Logo + current workflow stage */}
                <Box display="flex" alignItems="center" justifyContent="flex-start" sx={{ minWidth: 0, flexShrink: 0 }}>
                    <RouterLink to={VIEW_PATHS.home} aria-label="Home" style={{ display: 'inline-flex' }}>
                        <img
                            src={logo}
                            alt="Logo"
                            style={{
                                height: 60,
                                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.5)',
                                borderRadius: '8px',
                            }}
                        />
                    </RouterLink>
                    {appStage ? <AppStageBadge icon={appStage.icon} label={appStage.label} /> : null}
                </Box>

                {/* Admin tab when permitted — right-aligned in the header (before profile tools). Home is the logo. */}
                <Box display="flex" alignItems="flex-end" justifyContent="flex-end" sx={{ flexGrow: 1, pb: 0.5, pr: 1 }}>
                    <HeaderNavTabs currentView={currentView} showAdmin={showAdmin} />
                </Box>

                {/* Right: Analytic Profile Selector, Feedback & Account Icons */}
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1.5} sx={{ minWidth: '200px' }}>
                    <Box sx={{ 
                        '& .MuiSelect-select': { 
                            color: '#ddd !important' 
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.23) !important'
                        },
                        '& .MuiSvgIcon-root': {
                            color: '#ddd !important'
                        }
                    }}>
                        <AnalyticProfileSelector />
                    </Box>
                    <IconButton onClick={openFeedbackDrawer} sx={{ color: '#ddd' }} aria-label="Open feedback">
                        <FeedbackRoundedIcon style={{fontSize: 32}}/>
                    </IconButton>
                    <Box sx={{ 
                        '& .MuiSvgIcon-root': { 
                            color: '#ddd !important' 
                        }
                    }}>
                        <AccountIconButton onClick={openProfileDrawer}/>
                    </Box>
                </Box>

                <Drawer
                    anchor="right"
                    open={feedbackDrawerOpen}
                    onClose={() => setFeedbackDrawerOpen(false)}
                    PaperProps={{
                        sx: {
                            width: { xs: '100vw', sm: 'min(480px, 50vw)' },
                            maxWidth: { xs: '100vw', sm: '50vw' },
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: '#18181b',
                            borderLeft: '1px solid #27272a',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1.5,
                                borderBottom: '1px solid #27272a',
                                bgcolor: '#09090b',
                                flexShrink: 0,
                            }}
                        >
                            <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 'bold' }}>
                                Submit feedback
                            </Typography>
                            <IconButton onClick={() => setFeedbackDrawerOpen(false)} aria-label="Close" sx={{ color: '#fafafa' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                p: 2,
                            }}
                        >
                            {feedbackError && (
                                <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
                                    {feedbackError}
                                </Alert>
                            )}
                            <TextField
                                label="Title"
                                fullWidth
                                value={feedbackTitle}
                                onChange={(e) => setFeedbackTitle(e.target.value)}
                                sx={{
                                    flexShrink: 0,
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': { color: '#fafafa' },
                                    '& .MuiInputLabel-root': { color: '#a1a1aa' },
                                }}
                            />
                            <TextField
                                label="Description"
                                fullWidth
                                multiline
                                value={feedbackDescription}
                                onChange={(e) => setFeedbackDescription(e.target.value)}
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'stretch',
                                    '& .MuiInputBase-root': {
                                        flex: 1,
                                        alignItems: 'flex-start',
                                        py: 1,
                                    },
                                    '& textarea': {
                                        height: '100% !important',
                                        overflow: 'auto !important',
                                        boxSizing: 'border-box',
                                    },
                                    '& .MuiOutlinedInput-root': { color: '#fafafa', height: '100%' },
                                    '& .MuiInputLabel-root': { color: '#a1a1aa' },
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                p: 2,
                                borderTop: '1px solid #27272a',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                flexShrink: 0,
                                bgcolor: '#18181b',
                            }}
                        >
                            <Button
                                onClick={() => void handleFeedbackSubmit()}
                                variant="contained"
                                disabled={feedbackSubmitting}
                                sx={{ bgcolor: '#60a5fa', color: '#0a0a0a', '&:hover': { bgcolor: '#3b82f6' } }}
                            >
                                {feedbackSubmitting ? <CircularProgress size={24} sx={{ color: '#0a0a0a' }} /> : 'Submit'}
                            </Button>
                        </Box>
                    </Box>
                </Drawer>

                <Drawer
                    anchor="right"
                    open={profileDrawerOpen}
                    onClose={() => setProfileDrawerOpen(false)}
                    PaperProps={{
                        sx: {
                            width: { xs: '100vw', sm: 'min(400px, 40vw)' },
                            maxWidth: { xs: '100vw', sm: '440px' },
                            bgcolor: '#18181b',
                            borderLeft: '1px solid #27272a',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1.5,
                                borderBottom: '1px solid #27272a',
                                bgcolor: '#09090b',
                            }}
                        >
                            <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 'bold' }}>
                                Profile
                            </Typography>
                            <IconButton onClick={() => setProfileDrawerOpen(false)} aria-label="Close" sx={{ color: '#fafafa' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#fafafa' }} align="center">
                                {user?.username}
                            </Typography>
                            <Box sx={{ my: 2 }}>
                                <ProfilePicture />
                            </Box>
                            {user?.first_name && user?.last_name && (
                                <Typography variant="body1" align="center" sx={{ color: '#e4e4e7', mb: 2 }}>
                                    Welcome, {`${user.first_name} ${user.last_name}`}!
                                </Typography>
                            )}
                            <Button
                                variant="outlined"
                                size="small"
                                fullWidth
                                onClick={() => {
                                    setProfileDrawerOpen(false);
                                    setChangePasswordOpen(true);
                                }}
                                sx={{ color: '#fafafa', borderColor: '#3f3f46', textTransform: 'none', mt: 1, '&:hover': { borderColor: '#71717a' } }}
                            >
                                Change password
                            </Button>
                            <Button
                                variant="text"
                                size="small"
                                fullWidth
                                onClick={() => {
                                    // logout() clears only the session (tokens + cached user) and keeps per-user
                                    // preferences such as the selected analytic profile. Then drop the in-memory
                                    // user too; its stored copy is already gone, so UserContext's
                                    // stored-user-without-state guard doesn't reload the page.
                                    logout();
                                    setUser(null);
                                    setProfileDrawerOpen(false);
                                }}
                                sx={{ color: '#a1a1aa', textTransform: 'none', mt: 1 }}
                            >
                                Sign out
                            </Button>
                        </Box>
                    </Box>
                </Drawer>

                <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />

                {/* Snackbar for general feedback success */}
                <Snackbar
                    open={!!feedbackSuccess}
                    autoHideDuration={6000}
                    onClose={() => setFeedbackSuccess("")}
                    TransitionComponent={Collapse}
                >
                    {!!feedbackSuccess ? (
                        <Alert onClose={() => setFeedbackSuccess("")} severity="success" sx={{width: '100%'}}>
                            {feedbackSuccess}
                        </Alert>
                    ) : <></>}
                </Snackbar>

            </Box>
            {/* Tab Content */}
            <Box sx={{padding: 0}} flexGrow={1} minHeight="100vh">
                {renderCurrentView()}
            </Box>
            <Box component="footer" sx={{
                textAlign: 'center',
                backgroundColor: '#0a0a0a',
                color: '#888',
                padding: 2
            }}>
                &copy; {new Date().getFullYear()} Blacksheep Meedia. All rights reserved.
            </Box>
        </Box>
        </OpenRadarAttachContext.Provider>
    );
};

export default LayoutMain;
